import React, { useEffect, useState } from 'react';
import { createDeposit, getDeposits, getBonusTiers } from '../api';

const statusBadge = (st) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
    background: st === 'paid' || st === 'paid_over' ? '#d1fae5' : st === 'waiting' ? '#fef3c7' : '#fee2e2',
    color:      st === 'paid' || st === 'paid_over' ? '#065f46' : st === 'waiting' ? '#92400e' : '#991b1b',
});

// 2026-08-14: ampliata la lista con le crypto realmente supportate dal merchant
// Heleket (verificato via /v1/payment/services: 17 valute). USDC richiesto da Jan.
// Codici = quelli attesi da Heleket (to_currency passato raw al gateway).
const CRYPTOS = ['USDT', 'USDC', 'DAI', 'BTC', 'ETH', 'BNB', 'SOL', 'TON', 'TRX', 'LTC', 'DOGE', 'POL', 'AVAX', 'BCH'];

const s = {
    h1:    { fontSize: 22, fontWeight: 700, marginBottom: 20 },
    grid:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' },
    box:   { background: '#fff', borderRadius: 10, padding: 28, boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
    boxH:  { fontSize: 16, fontWeight: 700, marginBottom: 18, color: '#1a1d23' },
    label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, marginTop: 14 },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 },
    sel:   { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' },
    btn:   { marginTop: 20, width: '100%', padding: '12px 0', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: 'pointer' },
    err:   { marginTop: 10, color: '#ef4444', fontSize: 13 },
    ok:    { marginTop: 10, padding: 12, background: '#d1fae5', borderRadius: 8, fontSize: 13, color: '#065f46' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th:    { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #f3f4f6', color: '#6b7280', fontSize: 12, textTransform: 'uppercase' },
    td:    { padding: '10px 12px', borderBottom: '1px solid #f9fafb' },
    link:  { color: '#6366f1', textDecoration: 'none', fontSize: 12 },
    note:  { marginTop: 16, padding: '12px 16px', background: '#f0f4ff', borderRadius: 8, fontSize: 12, color: '#4b5563', lineHeight: 1.6 },
};

export default function Deposit() {
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState('USDT');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [payUrl, setPayUrl] = useState('');
    const [deposits, setDeposits] = useState([]);
    const [bonusTiers, setBonusTiers] = useState([]);
    const [eurUsd, setEurUsd] = useState(null);

    useEffect(() => {
        getDeposits().then(r => setDeposits(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        getBonusTiers().then(r => setBonusTiers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        // Fetch EUR/USD rate
        fetch('https://api.exchangerate-api.com/v4/latest/EUR')
            .then(r => r.json())
            .then(d => { if (d?.rates?.USD) setEurUsd(d.rates.USD); })
            .catch(() => {});
    }, []);

    // Find applicable bonus for current amount
    const amt = parseFloat(amount) || 0;
    const applicableBonus = bonusTiers
        .filter(t => amt >= parseFloat(t.min_amount))
        .sort((a, b) => b.min_amount - a.min_amount)[0] || null;

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setPayUrl('');
        const amt = parseFloat(amount);
        if (!amt || amt < 1) { setError('Minimum deposit is €1'); return; }
        setLoading(true);
        try {
            const res = await createDeposit(amt, currency);
            const url = res.data.payment_url;
            setPayUrl(url);
            window.open(url, '_blank');
            getDeposits().then(r => setDeposits(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create deposit');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <h1 style={s.h1}>Deposit Funds</h1>
            <div className="og-grid-2" style={s.grid}>
                {/* Form */}
                <div style={s.box}>
                    <div style={s.boxH}>Add Funds (Crypto)</div>
                    <form onSubmit={submit}>
                        <label style={s.label}>Amount (EUR)</label>
                        <input style={s.input} type="number" min="1" step="0.01" value={amount}
                            onChange={e => setAmount(e.target.value)}
                            placeholder="e.g. 50.00" required />
                        {/* Live EUR → USD conversion */}
                        {amt > 0 && eurUsd && (
                            <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                                ≈ ${(amt * eurUsd).toFixed(2)} USD
                            </div>
                        )}

                        <label style={s.label}>Cryptocurrency</label>
                        <select style={s.sel} value={currency} onChange={e => setCurrency(e.target.value)}>
                            {CRYPTOS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>

                        {/* Bonus tiers */}
                        {bonusTiers.length > 0 && (
                            <div style={{ marginTop: 16, padding: '14px 16px', background: '#faf5ff', borderRadius: 8, border: '1px solid #e9d5ff' }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8', marginBottom: 4 }}>Deposit Bonuses</div>
                                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10, lineHeight: 1.5 }}>
                                    Deposit more to earn bonus credits! Bonuses are automatically added to your balance.
                                </div>
                                {bonusTiers.map(t => {
                                    const minAmt = parseFloat(t.min_amount);
                                    const bonusEx = (minAmt * t.bonus_pct / 100).toFixed(2);
                                    const isActive = amt >= minAmt;
                                    return (
                                        <div key={t.min_amount} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '4px 0', color: isActive ? '#6d28d9' : '#9ca3af', fontWeight: isActive ? 700 : 400 }}>
                                            <span>{t.label || `Deposit ≥ €${t.min_amount}`}</span>
                                            <span>+{t.bonus_pct}% {isActive ? '✓' : ''}<span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400, marginLeft: 4 }}>(e.g. €{minAmt} → +€{bonusEx})</span></span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Live bonus preview */}
                        {applicableBonus && (
                            <div style={{ marginTop: 10, padding: '10px 14px', background: '#d1fae5', borderRadius: 8, border: '1px solid #6ee7b7', fontSize: 13 }}>
                                <strong style={{ color: '#065f46' }}>You receive +€{(amt * applicableBonus.bonus_pct / 100).toFixed(2)} bonus</strong>
                                <span style={{ color: '#047857', marginLeft: 6 }}>({applicableBonus.bonus_pct}% — {applicableBonus.label})</span>
                            </div>
                        )}

                        {error && <div style={s.err}>{error}</div>}
                        {payUrl && (
                            <div style={s.ok}>
                                Invoice created! <a href={payUrl} target="_blank" rel="noreferrer" style={{ color: '#065f46', fontWeight: 700 }}>
                                    Click to pay →
                                </a>
                            </div>
                        )}

                        <button style={{ ...s.btn, opacity: loading ? .6 : 1 }} disabled={loading}>
                            {loading ? 'Creating invoice...' : 'Generate Payment Link'}
                        </button>
                    </form>

                    <div style={s.note}>
                        You will be redirected to Heleket to complete the payment.
                        Funds are credited automatically after blockchain confirmation.
                        <br /><br />
                        Minimum deposit: €1. <strong>You pay only the amount shown</strong> — payment processing fees are covered by us.
                    </div>
                </div>

                {/* History */}
                <div style={s.box}>
                    <div style={s.boxH}>Deposit History</div>
                    {deposits.length === 0 ? (
                        <p style={{ color: '#9ca3af', fontSize: 14 }}>No deposits yet.</p>
                    ) : (
                        <div className="og-table-wrap">
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>Amount</th>
                                    <th style={s.th}>Crypto</th>
                                    <th style={s.th}>Status</th>
                                    <th style={s.th}>Date</th>
                                    <th style={s.th}>Pay</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deposits.map(d => (
                                    <tr key={d.order_id}>
                                        <td style={s.td}>€{parseFloat(d.amount).toFixed(2)}</td>
                                        <td style={s.td}>{d.to_currency}</td>
                                        <td style={s.td}><span style={statusBadge(d.status)}>{d.status}</span></td>
                                        <td style={s.td}>{new Date(d.created_at).toLocaleDateString()}</td>
                                        <td style={s.td}>
                                            {d.status === 'waiting' && d.payment_url && (
                                                <a href={d.payment_url} target="_blank" rel="noreferrer" style={s.link}>Pay →</a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
